import { useMemo } from 'react'
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  useMaterialReactTable,
} from 'material-react-table'
import { IPendingSubmissionWithMetadata } from './SubmissionsView'
import { Box, IconButton, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  ImageCell,
  NameCell,
  BrandCell,
  DescriptionCell,
  CountryCell,
  YearCell,
  PriceCell,
  PerfumerCell,
  NotesCell,
  DateCell,
} from '../components/TableCells'

interface SubmissionsTableProps {
  data: IPendingSubmissionWithMetadata[]
  isLoading: boolean
  pagination: MRT_PaginationState
  rowCount: number
  onPaginationChange: (updater: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => void
  onEdit: (submission: IPendingSubmissionWithMetadata) => void
  onApprove: (submissionId: string) => void
  onDeny: (submissionId: string) => void
  searchValue: string
  onSearchChange: (value: string) => void
}

export function SubmissionsTable({
  data,
  isLoading,
  pagination,
  rowCount,
  onPaginationChange,
  onEdit,
  onApprove,
  onDeny,
  searchValue,
  onSearchChange,
}: SubmissionsTableProps) {
  const columns = useMemo<MRT_ColumnDef<IPendingSubmissionWithMetadata>[]>(
    () => [
      {
        accessorKey: 'imgUrl',
        header: 'Image',
        size: 100,
        enableSorting: false,
        Cell: ({ cell }) => <ImageCell imgUrl={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: 'name',
        header: 'Fragrance Name',
        size: 200,
        Cell: ({ cell }) => <NameCell name={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'brand',
        header: 'Brand',
        size: 150,
        Cell: ({ row }) => <BrandCell brand={row.original.brand} brandText={row.original.brand_text} />,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        Cell: ({ cell }) => <DescriptionCell description={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'country',
        header: 'Country',
        size: 150,
        Cell: ({ row }) => <CountryCell country={row.original.country} countryText={row.original.country_text} />,
      },
      {
        accessorKey: 'year',
        header: 'Year',
        size: 100,
        Cell: ({ cell }) => <YearCell year={cell.getValue<number | null>()} />,
      },
      {
        accessorKey: 'perfumers',
        header: 'Perfumer',
        size: 150,
        Cell: ({ row }) => <PerfumerCell perfumers={row.original.perfumers} perfumersText={row.original.perfumers_text} />,
      },
      {
        accessorKey: 'price',
        header: 'Price',
        size: 100,
        Cell: ({ row }) => <PriceCell price={row.original.price} />,
      },
      {
        accessorKey: 'topNotes',
        header: 'Top Notes',
        size: 200,
        Cell: ({ row }) => <NotesCell notes={row.original.topNotes} notesText={row.original.topNotes_text} />,
      },
      {
        accessorKey: 'middleNotes',
        header: 'Middle Notes',
        size: 200,
        Cell: ({ row }) => <NotesCell notes={row.original.middleNotes} notesText={row.original.middleNotes_text} />,
      },
      {
        accessorKey: 'bottomNotes',
        header: 'Base Notes',
        size: 200,
        Cell: ({ row }) => <NotesCell notes={row.original.bottomNotes} notesText={row.original.bottomNotes_text} />,
      },
      {
        accessorKey: 'created',
        header: 'Submitted',
        size: 150,
        Cell: ({ cell }) => <DateCell date={cell.getValue<string>()} />,
      },
    ],
    [],
  )

  const table = useMaterialReactTable({
    columns,
    data,
    enableEditing: false,
    enableRowActions: true,
    positionActionsColumn: 'first',
    getRowId: (row) => row.id,
    manualPagination: true,
    rowCount,
    state: {
      isLoading,
      pagination,
      globalFilter: searchValue,
      columnPinning: {
        left: ['mrt-row-actions'],
      },
    },
    onPaginationChange,
    onGlobalFilterChange: onSearchChange,
    manualFiltering: true,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableGlobalFilter: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => onEdit(row.original)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Approve">
          <IconButton
            color="success"
            onClick={() => onApprove(row.original.id)}
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Deny">
          <IconButton
            color="error"
            onClick={() => onDeny(row.original.id)}
          >
            <CancelIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableContainerProps: {
      sx: {
        maxHeight: '100%',
        height: '100%',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: 'none',
      },
    },
  })

  return <MaterialReactTable table={table} />
}
